import React, { useEffect, useMemo, useState } from 'react'
import { ArrowRight, Mail, MapPin, Phone, Facebook } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { treatmentFocusMap } from '../data/navigation.js'
import { buildResponsiveImageProps, excerptFromHtml, getCmsPageText, stripHtml } from '../lib/cms.js'
import { ButtonLink, Card, Container, Pill, SectionHeading, SectionLabel } from './atoms.jsx'

export function SeoManager({ title, description, image, canonical, lang = 'da-DK' }) {
  useEffect(() => {
    document.title = title
    document.documentElement.lang = lang

    const upsert = (selector, attrs, content) => {
      let element = document.head.querySelector(selector)
      if (!element) {
        element = document.createElement(attrs.tag || 'meta')
        Object.entries(attrs).forEach(([key, value]) => {
          if (key !== 'tag') element.setAttribute(key, value)
        })
        document.head.appendChild(element)
      }

      if (content !== undefined && content !== null && content !== '') {
        element.setAttribute('content', content)
      } else if (element.tagName === 'META') {
        element.removeAttribute('content')
      }
      return element
    }

    upsert('meta[name="description"]', { tag: 'meta', name: 'description' }, description)
    upsert('meta[property="og:title"]', { tag: 'meta', property: 'og:title' }, title)
    upsert('meta[property="og:description"]', { tag: 'meta', property: 'og:description' }, description)
    upsert('meta[property="og:image"]', { tag: 'meta', property: 'og:image' }, image)
    upsert('meta[property="og:type"]', { tag: 'meta', property: 'og:type' }, 'website')
    upsert('meta[name="twitter:card"]', { tag: 'meta', name: 'twitter:card' }, image ? 'summary_large_image' : 'summary')
    upsert('meta[name="twitter:image"]', { tag: 'meta', name: 'twitter:image' }, image)

    let canonicalLink = document.head.querySelector('link[rel="canonical"]')
    if (!canonicalLink) {
      canonicalLink = document.createElement('link')
      canonicalLink.setAttribute('rel', 'canonical')
      document.head.appendChild(canonicalLink)
    }
    canonicalLink.setAttribute('href', canonical)
  }, [title, description, image, canonical, lang])

  return null
}

export function RichContent({ html, className }) {
  const navigate = useNavigate()

  return (
    <div
      className={className}
      onClick={(event) => {
        const anchor = event.target.closest('a')
        if (!anchor) return

        const href = anchor.getAttribute('href') || ''
        if (href.startsWith('/') && !href.startsWith('//')) {
          event.preventDefault()
          navigate(href)
        }
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

function StoryCard({ story }) {
  const image = story?.image?.[0]
  const content = excerptFromHtml(getCmsPageText(story), 130) || story?.summary || ''

  const femaleNames = [
    'karen', 'dyveke', 'bodil', 'joan', 'pernille', 'charlotte',
    'zivile', 'doris', 'astrid', 'birthe', 'stine', 'caroline',
    'heidi', 'aase', 'zunja', 'tanja', 'iben', 'bettina', 'dorte', 'grethe'
  ]
  const isFemale = femaleNames.some((name) => (story?.title || '').toLowerCase().includes(name))
  const fallbackImage = isFemale 
    ? 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/woman-avatar-copy.jpg'
    : 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/man-avatar-copy.jpg'
  const imageUrl = image || fallbackImage
  const slug = story?.slug?.da || story?.slug || ''
  const topic = story?.summary || (story?.terms?.treatment?.[0] ? story.terms.treatment[0] : '')

  return (
    <Card className="flex h-full flex-col gap-3.5 p-5 bg-white border border-stone-200 shadow-xs hover:shadow-md transition-all">
      <div className="flex items-start gap-3.5 pt-1">
        <img
          src={buildResponsiveImageProps(imageUrl, [180, 360]).src}
          srcSet={buildResponsiveImageProps(imageUrl, [180, 360]).srcSet}
          sizes="72px"
          alt={story.title}
          className="h-16 w-16 sm:h-20 sm:w-20 shrink-0 rounded-full object-cover border-2 border-forest-100 bg-stone-100"
        />
        <div className="space-y-1.5 min-w-0 flex-1">
          {topic && (
            <span
              className="inline-block text-[11px] sm:text-xs font-semibold px-2 py-1 rounded bg-forest-50 text-forest-800 border border-forest-200/70 leading-snug break-words"
              title={topic}
            >
              {topic}
            </span>
          )}
          <h3 className="text-base sm:text-lg font-bold text-forest-950 leading-snug">
            {story.title}
          </h3>
        </div>
      </div>
      <div className="flex-1">
        <p className="text-xs sm:text-sm leading-relaxed text-stone-600 line-clamp-3">{content}</p>
      </div>
      <div className="mt-auto pt-2 border-t border-stone-100">
        <ButtonLink href={`/patienthistorie/${slug}`} variant="ghost" className="px-0 text-xs font-semibold text-forest-800 hover:text-forest-950 flex items-center gap-1">
          Læs hele historien <ArrowRight className="h-3.5 w-3.5" />
        </ButtonLink>
      </div>
    </Card>
  )
}

export function StoryMarquee({ stories = [], title = 'Patienthistorier', description, hideHeading = false }) {
  const items = useMemo(() => (stories.length ? stories : []), [stories])
  const track = [...items, ...items]

  if (!items.length) return null

  return (
    <section className="overflow-hidden py-4 w-full">
      {!hideHeading && (
        <div className="mb-8 flex flex-col items-center justify-center text-center">
          <SectionHeading eyebrow="Patienthistorier" title={title} description={description} align="center" />
        </div>
      )}
      <div className="relative overflow-hidden w-full">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-stone-50 to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-stone-50 to-transparent" />
        <div className="story-marquee-track flex min-w-full w-max gap-5 py-2">
          {track.map((story, index) => (
            <div key={`${story.id ?? story.slug?.da ?? story.title}-${index}`} className="w-[min(100vw-2rem,18rem)] shrink-0 sm:w-[18.5rem] md:w-[20rem] lg:w-[21.5rem] xl:w-[22.5rem]">
              <StoryCard story={story} />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export function GalleryGrid({ images = [], title = 'Galleri', description }) {
  const [activeIndex, setActiveIndex] = useState(null)

  const active = activeIndex !== null ? images[activeIndex] : null

  return (
    <section>
      <div className="mb-8">
        <SectionHeading eyebrow="Galleri" title={title} description={description} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {images.map((image, index) => {
          const imageProps = buildResponsiveImageProps(image.url, [360, 720, 1000])
          return (
            <button
              key={`${image.url}-${index}`}
              type="button"
              onClick={() => setActiveIndex(index)}
              className="group overflow-hidden rounded-none border border-white/70 bg-white shadow-[0_18px_50px_rgba(31,50,42,0.08)]"
            >
              <img
                src={imageProps.src}
                srcSet={imageProps.srcSet}
                sizes="(min-width: 1280px) 25vw, (min-width: 768px) 33vw, 50vw"
                alt={image.alt || 'Galleri-billede'}
                className="h-64 w-full object-cover transition duration-500 group-hover:scale-[1.03]"
              />
            </button>
          )
        })}
      </div>

      {active ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-forest-950/80 p-4 backdrop-blur-sm"
          onClick={() => setActiveIndex(null)}
        >
          <div className="relative max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-none bg-black shadow-[0_24px_80px_rgba(0,0,0,0.4)]" onClick={(event) => event.stopPropagation()}>
            <img src={buildResponsiveImageProps(active.url, [1000, 1400, 1800]).src} alt={active.alt || 'Galleri-billede'} className="max-h-[80vh] w-full object-contain" />
            <button
              type="button"
              className="absolute right-4 top-4 rounded-full bg-white/90 px-4 py-2 text-sm font-semibold text-forest-900"
              onClick={() => setActiveIndex(null)}
            >
              Luk
            </button>
          </div>
        </div>
      ) : null}
    </section>
  )
}

export function ContactForm() {
  const [form, setForm] = useState({ name: '', phone: '', email: '', description: '', website_url: '' })
  const [submitting, setSubmitting] = useState(false)
  const [status, setStatus] = useState({ type: '', message: '' })

  const validation = useMemo(() => {
    const errors = {}
    if (!form.name.trim()) errors.name = 'Skriv venligst dit navn.'
    if (!form.phone.trim()) errors.phone = 'Skriv venligst dit telefonnummer.'
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Skriv venligst en gyldig email.'
    if (!form.description.trim()) errors.description = 'Skriv venligst en besked.'
    return errors
  }, [form])

  async function handleSubmit(event) {
    event.preventDefault()
    setStatus({ type: '', message: '' })

    if (Object.keys(validation).length) {
      setStatus({ type: 'error', message: 'Ret venligst felterne markeret herunder.' })
      return
    }

    try {
      setSubmitting(true)
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })

      if (!response.ok) {
        throw new Error('Formularen kunne ikke sendes. Prøv igen om lidt.')
      }

      setStatus({ type: 'success', message: 'Tak for din besked. Vi vender tilbage hurtigst muligt.' })
      setForm({ name: '', phone: '', email: '', description: '', website_url: '' })
    } catch (error) {
      setStatus({ type: 'error', message: error instanceof Error ? error.message : 'Der opstod en fejl.' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card className="space-y-6">
      <div>
        <SectionLabel>Kontaktformular</SectionLabel>
        <h3 className="mt-2 font-serif text-2xl text-forest-900">Skriv til os</h3>
        <p className="mt-2 text-sm leading-7 text-stone-600">Udfyld formularen, så vender vi tilbage så hurtigt som muligt.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="block space-y-2 text-sm font-medium text-stone-700">
          Navn
          <input
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            className="w-full rounded-none border border-forest-200 bg-white px-4 py-3 text-stone-900 outline-none transition placeholder:text-stone-400 focus:border-forest-500 focus:ring-2 focus:ring-forest-200"
            placeholder="Dit navn"
            autoComplete="name"
          />
        </label>

        <label className="block space-y-2 text-sm font-medium text-stone-700">
          Telefonnummer
          <input
            value={form.phone}
            onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
            className="w-full rounded-none border border-forest-200 bg-white px-4 py-3 text-stone-900 outline-none transition placeholder:text-stone-400 focus:border-forest-500 focus:ring-2 focus:ring-forest-200"
            placeholder="(+45) 86 38 41 17"
            autoComplete="tel"
          />
        </label>

        <label className="block space-y-2 text-sm font-medium text-stone-700">
          Email
          <input
            value={form.email}
            onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
            className="w-full rounded-none border border-forest-200 bg-white px-4 py-3 text-stone-900 outline-none transition placeholder:text-stone-400 focus:border-forest-500 focus:ring-2 focus:ring-forest-200"
            placeholder="navn@eksempel.dk"
            autoComplete="email"
          />
        </label>

        <label className="block space-y-2 text-sm font-medium text-stone-700">
          Beskrivelse
          <textarea
            value={form.description}
            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
            rows={6}
            className="w-full rounded-none border border-forest-200 bg-white px-4 py-3 text-stone-900 outline-none transition placeholder:text-stone-400 focus:border-forest-500 focus:ring-2 focus:ring-forest-200"
            placeholder="Fortæl kort om dine ønsker eller gener"
          />
        </label>

        <div className="sr-only absolute opacity-0 pointer-events-none">
          <label>
            Website
            <input
              value={form.website_url}
              onChange={(event) => setForm((current) => ({ ...current, website_url: event.target.value }))}
              name="website_url"
              type="text"
              autoComplete="off"
              tabIndex={-1}
            />
          </label>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 rounded-none bg-forest-900 px-5 py-3 text-sm font-semibold text-sand-50 transition hover:bg-forest-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? 'Sender…' : 'Send besked'}
            <ArrowRight className="h-4 w-4" />
          </button>
          <p className="text-sm text-stone-500">Telefon og email deles ikke over to linjer.</p>
        </div>

        {status.message ? (
          <p className={status.type === 'success' ? 'text-sm font-medium text-forest-800' : 'text-sm font-medium text-terracotta-600'}>{status.message}</p>
        ) : null}
      </form>
    </Card>
  )
}

export function QuickLinksGrid({ items }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {items.map((item) => {
        const image = item.image?.[0]
        const imageProps = image ? buildResponsiveImageProps(image, [480, 800, 1200]) : null
        return (
          <Card key={item.href} className="overflow-hidden p-0">
            {imageProps ? <img src={imageProps.src} srcSet={imageProps.srcSet} sizes="(min-width: 1280px) 25vw, (min-width: 768px) 50vw, 100vw" alt={item.label} className="h-52 w-full object-cover" /> : null}
            <div className="space-y-4 p-5">
              <div>
                <h3 className="text-lg font-semibold text-forest-900">{item.label}</h3>
                <p className="mt-2 text-sm leading-6 text-stone-600">{item.description}</p>
              </div>
              <ButtonLink href={item.href} variant="secondary">
                Læs mere <ArrowRight className="h-4 w-4" />
              </ButtonLink>
            </div>
          </Card>
        )
      })}
    </div>
  )
}

export function InfoStrip({ items }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {items.map((item) => (
        <Card key={item.title} className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-none bg-forest-100 text-forest-800">{item.icon}</div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-forest-700">{item.title}</p>
            <p className="mt-2 text-sm leading-6 text-stone-600">{item.text}</p>
          </div>
        </Card>
      ))}
    </div>
  )
}

export function RelatedStories({ slug, stories }) {
  const targetTreatments = treatmentFocusMap[slug] || []
  const filtered = stories.filter((story) => (story?.terms?.treatment || []).some((term) => targetTreatments.includes(term)))

  if (!filtered.length) return null

  return (
    <section className="mt-16">
      <div className="mb-6">
        <SectionHeading eyebrow="Patienthistorier" title="Relaterede historier" description="Udvalgte historier fra CMS, koblet til den aktuelle side." />
      </div>
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {filtered.slice(0, 3).map((story) => (
          <StoryCard key={story.id ?? story.slug?.da} story={story} />
        ))}
      </div>
    </section>
  )
}

export function PageHero({ page, kicker, title, description, actions = [], reverse = false, currentSlide: externalCurrentSlide, isDark = true }) {
  const isHome = !page || page?.slug?.da === 'index' || page?.slug === 'index' || page?.slug?.da === 'forside' || page?.slug === 'forside'

  const slides = [
    {
      text: "Vi er blandt landets mest erfarne akupunktører",
      image: "https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/akupunktur-1.jpg"
    },
    {
      text: "Vi behandler alle aldre, små og store",
      image: "https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/barn.png"
    },
    {
      text: "Vejen til energi og bedre helbred, balance mellem krop og sind",
      image: "https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/skov.png"
    },
    {
      text: "Chi Kung Workshop i skoven",
      image: "https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/imgi-23-chikung2.png"
    },
    {
      text: "Vi er instruktører i Zhan Zhuang Chi Kung",
      image: "https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/chikung1.png"
    },
    {
      text: "Helseklinikken i Gjerrild  -  balance i krop og sind",
      image: "https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/helseklinikken.png"
    }
  ]

  const [internalCurrentSlide, setInternalCurrentSlide] = useState(0)
  const currentSlide = externalCurrentSlide !== undefined ? externalCurrentSlide : internalCurrentSlide

  useEffect(() => {
    if (!isHome || externalCurrentSlide !== undefined) return
    const interval = setInterval(() => {
      setInternalCurrentSlide((prev) => (prev + 1) % slides.length)
    }, 4000)
    return () => clearInterval(interval)
  }, [isHome, externalCurrentSlide])

  return (
    <div className="relative w-full">
      {isHome && externalCurrentSlide === undefined && (
        <div className="absolute -inset-x-4 -inset-y-10 lg:-inset-x-8 lg:-inset-y-16 z-0 overflow-hidden pointer-events-none">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentSlide ? 'opacity-25' : 'opacity-0'
              }`}
            >
              <img
                src={slide.image}
                alt={slide.text}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-sand-50 via-sand-50/90 to-sand-50/40" />
            </div>
          ))}
        </div>
      )}

      <div className="relative z-10 max-w-4xl mx-auto text-center space-y-6 py-4">
        <div className="space-y-4" style={(isHome || isDark) ? { textShadow: '0 1px 4px rgba(0, 0, 0, 0.6)' } : undefined}>
          <h1 className={`font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight ${isHome || isDark ? 'text-white' : 'text-forest-900'}`}>
            {isHome ? "Vi tilbyder behandling, workshops og foredrag" : (title || page?.title || kicker || "Helseklinikken")}
          </h1>

          {isHome ? (
            <div className="relative h-24 sm:h-20 flex items-center overflow-hidden">
              {slides.map((slide, index) => {
                let positionClass = 'opacity-0 translate-x-16 pointer-events-none'
                if (index === currentSlide) {
                  positionClass = 'opacity-100 translate-x-0'
                } else if (index === (currentSlide - 1 + slides.length) % slides.length) {
                  positionClass = 'opacity-0 -translate-x-16 pointer-events-none'
                }
                return (
                  <div
                    key={index}
                    className={`absolute w-full transition-all duration-1000 ease-in-out transform ${positionClass}`}
                  >
                    <p className="font-serif text-lg sm:text-xl md:text-2xl lg:text-3xl text-sand-200 font-semibold tracking-tight leading-tight">
                      {slide.text}
                    </p>
                  </div>
                )
              })}
            </div>
          ) : (
            description && (
              <p className={`text-lg leading-relaxed max-w-2xl mx-auto whitespace-pre-line ${isDark ? 'text-sand-100' : 'text-stone-600'}`}>
                {description}
              </p>
            )
          )}
        </div>

        {actions.length ? (
          <div className="flex flex-wrap justify-center gap-3">
            {actions.map((action) => (
              <ButtonLink key={action.href} href={action.href} variant={action.variant || 'primary'}>
                {action.label}
              </ButtonLink>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export function ContactDetails() {
  return (
    <Card className="space-y-5">
      <div>
        <SectionLabel>Kontakt</SectionLabel>
      </div>
      <div className="space-y-3 text-sm leading-7 text-stone-600">
        <p>Dyrehavevej 11</p>
        <p>8500 Grenå</p>
        <p className="whitespace-nowrap">Telefon: <a href="tel:86384117" className="font-semibold text-forest-900">(+45) 86 38 41 17</a></p>
        <p className="whitespace-nowrap">Klinik mobil: <a href="tel:23114117" className="font-semibold text-forest-900">(+45) 23 11 41 17</a></p>
        <p className="whitespace-nowrap">Email: <a href="mailto:info@helselarsen.dk" className="font-semibold text-forest-900">info@helselarsen.dk</a></p>
        <p>CVR nr.: 20563605</p>
      </div>
      <div className="flex flex-wrap gap-3">
        <ButtonLink href="tel:86384117" variant="primary"><Phone className="h-4 w-4" /> Ring fastnet</ButtonLink>
        <ButtonLink href="tel:23114117" variant="primary"><Phone className="h-4 w-4" /> Ring mobil</ButtonLink>
        <ButtonLink href="mailto:info@helselarsen.dk" variant="secondary"><Mail className="h-4 w-4" /> Mail</ButtonLink>
        <ButtonLink
          href="https://www.facebook.com/profile.php?id=100057487268744#"
          target="_blank"
          rel="noopener noreferrer"
          variant="secondary"
        >
          <Facebook className="h-4 w-4" /> Facebook
        </ButtonLink>
      </div>
    </Card>
  )
}
